import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, IconButton, Avatar } from '@mui/material';
import { ChevronDown, ChevronRight, User } from 'lucide-react';
import { WorkstreamBar } from './WorkstreamBar';
import { Workstream, WorkstreamFormData, Milestone, Assignee } from './types';
import { TimelineView } from './utils/timelineUtils';
import { useStatuses } from './hooks/useStatuses';
import { useWorkstreamTypes } from './hooks/useWorkstreamTypes';
import { useWorkspaceContext } from './context/WorkspaceContext';
import ParentWorkstreamBar from './ParentWorkstreamBar';

interface WorkstreamListProps {
  workstreams: Workstream[];
  timelineConfig: {
    view: TimelineView;
    zoomLevel: number;
    startDate: Date;
    endDate: Date;
  };
  today: Date;
  assignees: Assignee[];
  onDelete: (id: string) => Promise<void>;
  onEdit: (workstream: Workstream) => void;
  onUpdate: (id: string, data: Partial<WorkstreamFormData>) => Promise<any>;
  onUpdateMilestones: (workstreamId: string, milestones: Milestone[]) => Promise<void>;
}

export const WorkstreamList: React.FC<WorkstreamListProps> = ({
  workstreams,
  timelineConfig,
  today,
  assignees,
  onDelete,
  onEdit,
  onUpdate,
  onUpdateMilestones
}) => {
  const { workspaceId } = useWorkspaceContext();
  const { statuses } = useStatuses(workspaceId);
  const { workstreamTypes } = useWorkstreamTypes(workspaceId);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  const handleToggleExpand = (parentId: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  const handleWorkstreamUpdate = async (workstream: Workstream) => {
    const { id, ...rest } = workstream;
    const data: Partial<WorkstreamFormData> = {
      ...rest,
      startDate: new Date(workstream.startDate),
      endDate: new Date(workstream.endDate)
    };
    await onUpdate(id, data);
  };

  const parentWorkstreams = workstreams.filter(w => w.isParent);
  const childWorkstreams = workstreams.filter(w => !w.isParent);

  const getStatusConfig = (statusName: string) => {
    const status = statuses.find(s => s.name === statusName);
    if (!status) {
      return {
        label: 'Unknown',
        variant: 'outlined' as const,
        color: '#666'
      };
    }
    // Map 'default' to 'filled' if it somehow exists in old data
    const variant = status.variant === 'default' ? 'filled' : status.variant;
    return {
      ...status,
      variant
    };
  };

  const getTypeConfig = (typeName: string) => {
    return workstreamTypes.find(t => t.name === typeName) || {
      label: 'Unknown',
      color: '#666'
    };
  };

  const renderWorkstreamItem = (workstream: Workstream, isChild: boolean = false) => {
    const statusConfig = getStatusConfig(workstream.status);
    const typeConfig = getTypeConfig(workstream.type);
    const assignee = assignees.find(a => a.id === workstream.assigneeId);

    return (
      <Box
  key={workstream.id}
  sx={{
    display: 'flex',
    alignItems: 'center',
    p: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    borderLeft: isChild 
      ? theme => `4px solid ${theme.palette.primary.light}` 
      : 'none',
    '&:hover': {
      bgcolor: theme => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)'
    }
  }}
>
        {/* Name & Type Column */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 2, minWidth: 0 }}>
          {workstream.isParent && (
            <IconButton
              size="small"
              onClick={() => handleToggleExpand(workstream.id)}
              sx={{ mr: 1 }}
            >
              {expandedParents.has(workstream.id) ? (
                <ChevronDown size={18} />
              ) : (
                <ChevronRight size={18} />
              )}
            </IconButton>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {workstream.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: typeConfig.color,
                  flexShrink: 0
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {typeConfig.label}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Assignee Column */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Chip
            icon={assignee?.avatarUrl ? (
              <Avatar 
                src={assignee.avatarUrl} 
                sx={{ width: 20, height: 20 }}
              />
            ) : (
              <User size={14} />
            )}
            label={assignee?.name || 'Unassigned'}
            size="small"
            variant="outlined"
            sx={{ 
              maxWidth: '100%',
              height: 24,
              fontSize: '0.75rem'
            }}
            onClick={() => onEdit(workstream)}
          />
        </Box>

        {/* Status Column */}
        <Box sx={{ flex: 1 }}>
          <Chip
            label={statusConfig.label}
            size="small"
            variant={statusConfig.variant as 'outlined' | 'filled'}
            sx={{ 
              maxWidth: '100%',
              height: 24,
              fontSize: '0.75rem',
              backgroundColor: statusConfig.variant === 'filled' ? statusConfig.color : 'transparent',
              borderColor: statusConfig.color,
              color: statusConfig.variant === 'filled' ? 'white' : statusConfig.color
            }}
          />
        </Box>

        {/* Timeline */}
        <Box sx={{ flex: 3, position: 'relative', ml: 2 }}>
          {workstream.isParent ? (
            <ParentWorkstreamBar
              workstream={workstream}
              childWorkstreams={childWorkstreams.filter(child => child.parentId === workstream.id)}
              timelineConfig={timelineConfig}
            />
          ) : (
            <WorkstreamBar
              workstream={workstream}
              workstreams={workstreams}
              timelineConfig={timelineConfig}
              assignee={assignee || null}
              today={today}
              onDelete={onDelete}
              onEdit={onEdit}
              onUpdate={handleWorkstreamUpdate}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Paper 
      sx={{ 
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)'
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 2, color: 'text.secondary' }}>
          Name & Type
        </Typography>
        <Typography variant="subtitle2" sx={{ flex: 1, color: 'text.secondary' }}>
          Assignee
        </Typography>
        <Typography variant="subtitle2" sx={{ flex: 1, color: 'text.secondary' }}>
          Status
        </Typography>
        <Typography variant="subtitle2" sx={{ flex: 3, ml: 2, color: 'text.secondary' }}>
          Timeline
        </Typography>
      </Box>

      {/* Workstream List */}
      <Box sx={{ minWidth: timelineConfig.zoomLevel * 100 * 12 }}>
        {parentWorkstreams.map(parent => (
          <React.Fragment key={parent.id}>
            {renderWorkstreamItem(parent)}
            {expandedParents.has(parent.id) && 
              childWorkstreams
                .filter(child => child.parentId === parent.id)
                .map(child => renderWorkstreamItem(child, true))
            }
          </React.Fragment>
        ))}
        {childWorkstreams
          .filter(child => !child.parentId)
          .map(workstream => renderWorkstreamItem(workstream))}
      </Box>
    </Paper>
  );
};